import { ResultListBannerState } from "@stenajs-webui/elements";
import {
  createEditableEntityActions,
  createEditableEntityReducer,
  createEntityActions,
  createEntityListActions,
  createEntityListReducer,
  createEntityReducer,
  createRecordObjectActions,
  createRecordObjectReducer,
  EditableEntityRevertEditableEntityAction,
  EditableEntitySetEditableEntityFieldsAction,
  EditableEntityState,
  EntityListState,
  EntityState,
  RecordObjectState,
  reducerIdGate,
  reducerIdGateAction,
} from "@stenajs-webui/redux";
import { combineReducers } from "redux";
import { StoreState } from "@/redux/RootReducer";
import { ExpectedVolumeTableRow } from "../types";

interface RoutePairMeta {
  hasVolume: boolean;
}

interface ExpectedVolumeEntity {
  routePairMeta: EntityState<RoutePairMeta>;
  volumes: EntityListState<EditableEntityState<ExpectedVolumeTableRow>>;
}

export type ExpectedVolumesByRoutePair =
  RecordObjectState<ExpectedVolumeEntity>;

export const expectedVolumeReducerId = "expectedVolume";

export interface ExpectedVolumeState {
  expectedVolumesByRoutePair: RecordObjectState<{
    routePairMeta: EntityState<RoutePairMeta>;
    volumes: EntityListState<EditableEntityState<ExpectedVolumeTableRow>>;
  }>;
}

export const expectedVolumeReducer = reducerIdGate(
  expectedVolumeReducerId,
  combineReducers({
    expectedVolumesByRoutePair: createRecordObjectReducer(
      combineReducers({
        routePairMeta: createEntityReducer<RoutePairMeta>({
          hasVolume: false,
        }),
        volumes: createEntityListReducer(
          createEditableEntityReducer<ExpectedVolumeTableRow>({
            id: "",
            customerIsParent: false,
            customerIndex: "",
            customerNo: "",
            customerName: "",
            volumeAgreed: false,
          })
        ),
      })
    ),
  })
);

export const expectedVolumeSelectors = {
  getState: (state: StoreState): ExpectedVolumeState => state.expectedVolume,
  getExpectedVolumeForRoutePairId: (
    state: StoreState,
    routePairId: string
  ): ExpectedVolumeEntity | undefined =>
    state.expectedVolume.expectedVolumesByRoutePair[routePairId],
  getExpectedVolumesAreAllEmpty: (state: StoreState): boolean =>
    Object.values(state.expectedVolume.expectedVolumesByRoutePair).every(
      (entry) => !entry.routePairMeta.hasVolume
    ),
  getIsVolumeAgreedByRoutePair: (state: StoreState, routePairId: string) =>
    state.expectedVolume.expectedVolumesByRoutePair[routePairId]?.volumes.some(
      (volume) => volume.editable.volumeAgreed === true
    ) ?? false,
};

export const expectedVolumeActions = {
  clearAll: () =>
    reducerIdGateAction(
      expectedVolumeReducerId,
      createRecordObjectActions().clearAllRecords()
    ),
  setRowsForRoutePair: (
    routePairId: string,
    rows: Array<ExpectedVolumeTableRow>
  ) =>
    reducerIdGateAction(
      expectedVolumeReducerId,
      createRecordObjectActions().recordAction(
        routePairId,
        createEntityListActions<
          EditableEntityState<ExpectedVolumeTableRow>
        >().setList(
          rows.map((r) => ({
            id: r.id,
            editable: r,
            persisted: r,
          }))
        )
      )
    ),
  setRoutePairMetaFields: (
    routePairId: string,
    fields: Partial<RoutePairMeta>
  ) =>
    reducerIdGateAction(
      expectedVolumeReducerId,
      createRecordObjectActions().recordAction(
        routePairId,
        createEntityActions<RoutePairMeta>().setEntityFields(fields)
      )
    ),
  setFieldsForRow: (
    routePairId: string,
    expectedVolumeId: string,
    fields: Partial<ExpectedVolumeTableRow>
  ) =>
    reducerIdGateAction(
      expectedVolumeReducerId,
      createRecordObjectActions().recordAction(
        routePairId,
        createEntityListActions<
          ExpectedVolumeTableRow,
          EditableEntitySetEditableEntityFieldsAction<ExpectedVolumeTableRow>
        >().actionByFieldsMatch(
          { id: expectedVolumeId },
          createEditableEntityActions<ExpectedVolumeTableRow>().setEditableEntityFields(
            fields
          )
        )
      )
    ),
  setVolumeAgreedForRoutePair: (routePairId: string, volumeAgreed: boolean) =>
    reducerIdGateAction(
      expectedVolumeReducerId,
      createRecordObjectActions().recordAction(
        routePairId,
        createEntityListActions<
          ExpectedVolumeTableRow,
          EditableEntitySetEditableEntityFieldsAction<ExpectedVolumeTableRow>
        >().actionOnAll(
          createEditableEntityActions<ExpectedVolumeTableRow>().setEditableEntityFields(
            {
              volumeAgreed,
            }
          )
        )
      )
    ),
  clearExpectedVolume: (routePairId: string) =>
    reducerIdGateAction(
      expectedVolumeReducerId,
      createRecordObjectActions().recordAction(
        routePairId,
        createEntityListActions<
          ExpectedVolumeTableRow,
          EditableEntitySetEditableEntityFieldsAction<ExpectedVolumeTableRow>
        >().actionOnAll(
          createEditableEntityActions<ExpectedVolumeTableRow>().setEditableEntityFields(
            {
              accompaniedExpectedVolPeak: undefined,
              accompaniedExpectedVolShoulder: undefined,
              accompaniedExpectedVolOffPeak: undefined,
              unaccompaniedExpectedVolPeak: undefined,
              unaccompaniedExpectedVolShoulder: undefined,
              unaccompaniedExpectedVolOffPeak: undefined,
              volumeAgreed: false,
            }
          )
        )
      )
    ),
  revertAllRowsToPersistedState: (routePairId: string) =>
    reducerIdGateAction(
      expectedVolumeReducerId,
      createRecordObjectActions().recordAction(
        routePairId,
        createEntityListActions<
          ExpectedVolumeTableRow,
          EditableEntityRevertEditableEntityAction
        >().actionOnAll(
          createEditableEntityActions<ExpectedVolumeTableRow>().revertEditableEntity()
        )
      )
    ),
};
